/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React, {Fragment} from 'react';
import {getAppComponent, getMapComponent} from "../store/Accessor";
import {getAppState} from "../store/AppState";
import {LayerDef, MapStyle} from "../types";
import {$t} from "../lang/LangUtil";
import SingleColorChooser from "./palettes/SingleColorChooser";
import {XIcon} from "../ui/XIcon";
import {IconInfo} from "../util/IconInfo";
import {isValidNumber} from "../util/CommonUtil";
import {IconUtil} from "../util/IconUtil";
import {Theme} from "@ice/components";
import {LayerUtil} from "../map/LayerUtil";


interface LegendProps {
}


interface LegendState {
}


export class Legend extends React.Component<LegendProps, LegendState> {

    constructor(props: LegendProps) {
        super(props);
        this.state = {
        };
    }

    private toggleShowColor = (layerDef: LayerDef, value: any) => {
        let set = layerDef.config.hiddenColors;
        if (!set) {
            set = new Set<number>();
            layerDef.config.hiddenColors = set;
        }
        if (set.has(value)) {
            set.delete(value)
        } else {
            set.add(value);
        }
        getMapComponent().updateLayers(layerDef);
    }

    private renderColorPalette = (layerDef: LayerDef): JSX.Element => {
        const colorPalette = layerDef.config.colorPalette;
        const buckets = layerDef.dataset.createBucketsByField(layerDef.config.colorFieldId, colorPalette.length);
        const range = buckets.length > 1 ? buckets[buckets.length - 1] - buckets[0]: 0;

        const format = (n: number) => {
            const decs = range < 2 ? 2 : (range < 10 ? 1 : 0);
            const divider = Math.pow(10, decs);
            const v = (Math.round(n * divider) / divider);
            const f = v.toLocaleString('en-US');
            return v < 0 ? `(${f})` : String(f);
        };

        return (
            <Fragment>
                {
                    colorPalette && colorPalette.length > 0 &&
                    colorPalette.map((color, index) => {
                        if (index >= buckets.length - 1) {
                            return null;
                        } else {
                            const isHidden = layerDef.config.hiddenColors && layerDef.config.hiddenColors.has(index);
                            return (
                                <div className={"color"}>
                                    <XIcon
                                        icon={IconUtil.getPointShapeById(layerDef.config.shape).iconInfo}
                                        iconSize={"1.2em"}
                                        fill={color.toHex()}
                                    />
                                    <span
                                        style={{marginLeft: "0.5em"}}>{format(buckets[index])} - {index < buckets.length - 1 ? format(buckets[index + 1]) : "inf"}</span>
                                    <span style={{flexGrow: 1}}/>
                                    <span>
                                    <XIcon
                                        icon={isHidden ? IconInfo.NOEYE : IconInfo.EYE}
                                        iconSize={"1.2em"}
                                        onClick={() => this.toggleShowColor(layerDef, index)}
                                        title={$t(isHidden ? "show" : "hide")}
                                        css={(theme: Theme) => css`
                                            fill: ${isHidden ? theme.textColor.disabled : theme.selectionColor};
                                            cursor: pointer;
                                            margin-left: 0.5em;
                                        `}
                                    />
                                </span>
                                </div>
                            );
                        }
                    })
                }
            </Fragment>
        )
    }

    private renderColorMap = (layerDef: LayerDef): JSX.Element => {
        const colorMapEntries = Object.entries(LayerUtil.prepareColorMap(layerDef)).sort(([a], [b]) => a.localeCompare(b));

        return (
            <Fragment>
                {
                    colorMapEntries.map(([key, value]) => {
                        const isHidden = layerDef.config.hiddenColors && layerDef.config.hiddenColors.has(key);
                        return (
                            <div className={"color"}>
                                <XIcon
                                    icon={IconUtil.getPointShapeById(layerDef.config.shape).iconInfo}
                                    iconSize={"1.2em"}
                                    fill={value}
                                />
                                <span
                                    className={"ellipsis"}
                                    title={key}
                                    style={{maxWidth: "18em", marginLeft: "0.5em"}}
                                >
                                    {key === "" ? <b>{$t("defaultcolor")}</b> : key}
                                </span>
                                <span style={{flexGrow: 1}}/>
                                <span>
                                    <XIcon
                                        icon={isHidden ? IconInfo.NOEYE : IconInfo.EYE}
                                        iconSize={"1.2em"}
                                        onClick={() => this.toggleShowColor(layerDef, key)}
                                        title={$t(isHidden ? "show" : "hide")}
                                        css={(theme: Theme) => css`
                                            fill: ${isHidden ? theme.textColor.disabled : theme.selectionColor};
                                            cursor: pointer;
                                            margin-left: 0.5em;
                                        `}
                                    />
                                </span>
                            </div>
                        )
                    })
                }
            </Fragment>
        )
    }

    private renderLayerInfo = (layerDef: LayerDef): JSX.Element => {
        return (
            <div className={"layer"}>
                <div className={"header"}>
                    {$t("layer")} {layerDef.name}
                </div>
                { layerDef.isColorFieldNumeric() ? this.renderColorPalette(layerDef) : this.renderColorMap(layerDef) }
            </div>
        )
    }

    render = () => {
        return (
            <div key={`leg_${Date.now()}`}
                 className={"legend"}
                 style={{display: "flex", flexDirection: "column", position: "fixed", right: "0.4em", bottom: "0.4em"}}
            >
                <h1>
                    {$t("legend")}
                    <div style={{flexGrow: 1}} />
                    <XIcon icon={IconInfo.CLOSE} iconSize={"0.7em"} title={$t("hidelegend")} onClick={() => {
                        getAppState().setShowLegend(false);
                        getAppComponent().refresh();
                    }} />
                </h1>
                <div style={{overflowY: "auto"}}>
                    { getAppState().getLayerDefs().filter(def => !def.isHidden).map(def => this.renderLayerInfo(def)) }
                </div>
            </div>
        );
    }
}
